import { useLayoutEffect, useState } from 'react';
import clsx from 'clsx';
import type { PartialRecall, PositionDetail } from '@/types/trades.types';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToastStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';
import { formatDate } from '@/utils/format';
import {
  useSettlePartialRecall,
  useCancelPartialRecall,
  useSetPartialRecallSettlementDate,
} from '@/hooks/trades';

/** The fields the panel needs — a structural subset of PositionDetail. */
type PartialRecallsTarget = Pick<PositionDetail, 'id' | 'symbol_id' | 'partial_recalls'>;

interface PartialRecallsPanelProps {
  trade: PartialRecallsTarget;
}

const STATUS_BADGE: Record<PartialRecall['status'], string> = {
  pending: 'bg-amber-100 text-amber-800',
  settled: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

/**
 * LR-470: the booked partial recalls/returns on a trade, with the operator
 * controls product asked for — settle now (posts the partial reverse transfer +
 * reduces booked quantity), cancel a pending row (restores the markable
 * remaining), and override the contractual settlement date (so billing stays
 * correct on a delayed settle). Pending rows are actionable; settled/cancelled
 * rows are shown muted as history. Renders nothing when the trade has no recalls.
 */
export function PartialRecallsPanel({ trade }: PartialRecallsPanelProps) {
  const recalls = trade.partial_recalls ?? [];
  const { addToast } = useToastStore();

  // One active dialog at a time, keyed by the row + action.
  const [action, setAction] = useState<{ recall: PartialRecall; type: 'settle' | 'cancel' | 'date' } | null>(null);
  const [dateValue, setDateValue] = useState('');

  const settle = useSettlePartialRecall();
  const cancel = useCancelPartialRecall();
  const setDate = useSetPartialRecallSettlementDate();

  // Seed the date input + clear any prior error each time a dialog opens. Both the
  // "Edit date" and "Settle" dialogs surface the contractual settlement_date so the
  // operator sees it and can adjust before acting (LR-515: settle applies the edit
  // atomically, value-dating the BO reversal leg at the shown date). useLayoutEffect
  // commits the seed before paint so reopening on a different row never flashes the
  // previous row's date into a settlement-date field (money-sensitive).
  useLayoutEffect(() => {
    if (action?.type === 'date') {
      setDateValue(action.recall.settlement_date ?? '');
      setDate.reset();
    } else if (action?.type === 'settle') {
      setDateValue(action.recall.settlement_date ?? '');
      settle.reset();
    } else if (action?.type === 'cancel') {
      cancel.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  if (recalls.length === 0) return null;

  const close = () => setAction(null);

  const noun = (r: PartialRecall) => (r.kind === 'return' ? 'return' : 'recall');

  const confirmSettle = () => {
    if (!action || !dateValue) return;
    const r = action.recall;
    settle.mutate(
      { positionId: trade.id, recallId: r.id, settlementDate: dateValue },
      {
        onSuccess: () => {
          addToast('success', `Partial ${noun(r)} of ${r.quantity.toLocaleString('en-US')} settled`);
          close();
        },
        onError: (e) => addToast('error', getErrorMessage(e, `Failed to settle partial ${noun(r)}`)),
      },
    );
  };

  const confirmCancel = () => {
    if (!action) return;
    const r = action.recall;
    cancel.mutate(
      { positionId: trade.id, recallId: r.id },
      {
        onSuccess: () => {
          addToast('success', `Partial ${noun(r)} cancelled`);
          close();
        },
        onError: (e) => addToast('error', getErrorMessage(e, `Failed to cancel partial ${noun(r)}`)),
      },
    );
  };

  const confirmDate = () => {
    if (!action || !dateValue) return;
    const r = action.recall;
    setDate.mutate(
      { positionId: trade.id, recallId: r.id, settlementDate: dateValue },
      {
        onSuccess: () => {
          addToast('success', `Settlement date updated to ${formatDate(dateValue)}`);
          close();
        },
        onError: (e) => addToast('error', getErrorMessage(e, 'Failed to update settlement date')),
      },
    );
  };

  return (
    <section
      aria-labelledby="partial-recalls-heading"
      className="rounded-lg border border-lr-border bg-lr-surface shadow-sm"
    >
      <div className="border-b border-lr-border px-4 py-3">
        <h2 id="partial-recalls-heading" className="text-sm font-semibold text-gray-800">
          Partial Recalls / Returns
        </h2>
        <p className="mt-0.5 text-[11px] text-gray-500">
          Pending rows reduce the markable remaining quantity; settling reduces the booked quantity
          and posts a partial reverse transfer to Backoffice.
        </p>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
            <th className="px-4 py-2 font-medium">Type</th>
            <th className="px-4 py-2 font-medium text-right">Quantity</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Settlement date</th>
            <th className="px-4 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {recalls.map((r) => {
            const isPending = r.status === 'pending';
            return (
              <tr key={r.id} className="border-t border-lr-border/60">
                <td className="px-4 py-2 text-gray-800">{r.kind_display}</td>
                <td className="px-4 py-2 text-right font-mono tabular-nums text-gray-800">
                  {r.quantity.toLocaleString('en-US')}
                </td>
                <td className="px-4 py-2">
                  <span className={clsx('rounded px-1.5 py-0.5 text-[11px] font-medium', STATUS_BADGE[r.status])}>
                    {r.status_display}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-700">
                  {r.settlement_date ? (
                    formatDate(r.settlement_date)
                  ) : (
                    <span className="text-gray-400">Manual</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {isPending ? (
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setAction({ recall: r, type: 'date' })}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        Edit date
                      </button>
                      <button
                        type="button"
                        onClick={() => setAction({ recall: r, type: 'settle' })}
                        className="rounded bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                      >
                        Settle
                      </button>
                      <button
                        type="button"
                        onClick={() => setAction({ recall: r, type: 'cancel' })}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="text-right text-[11px] text-gray-400">
                      {r.status === 'settled' && r.settled_at ? formatDate(r.settled_at) : '—'}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ConfirmDialog
        show={action?.type === 'settle'}
        onClose={close}
        title="Settle partial recall"
        confirmLabel="Settle"
        confirmColor="green"
        isLoading={settle.isPending}
        confirmDisabled={!dateValue}
        onConfirm={confirmSettle}
        error={settle.error}
        errorFallback="Failed to settle partial recall."
        // LR-581: the BO reason (400 refusal / 502 outage) arrives in `detail`. The
        // dialog stays open on error, so this is where the operator reads it — the
        // toast below shares getErrorMessage's 500-char cap.
        showResponseDetail
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Settle {action ? noun(action.recall) : 'recall'} of{' '}
            <strong>{action?.recall.quantity.toLocaleString('en-US')}</strong> on{' '}
            <strong>{trade.symbol_id}</strong>? This reduces the booked quantity and posts a partial
            reverse transfer to Backoffice. Fully recalling the booked quantity closes the position.
          </p>
          <div>
            <label htmlFor="recall-settle-date" className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
              Settlement date <span className="normal-case text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              id="recall-settle-date"
              type="date"
              required
              value={dateValue}
              onChange={(e) => setDateValue(e.target.value)}
              aria-required="true"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              The Backoffice reverse transfer is value-dated at this date. Pre-filled with the
              contractual date; adjust it here if settlement is happening on a different day.
            </p>
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        show={action?.type === 'cancel'}
        onClose={close}
        title="Cancel partial recall"
        titleColor="red"
        confirmLabel="Cancel recall"
        confirmColor="red"
        isLoading={cancel.isPending}
        onConfirm={confirmCancel}
        error={cancel.error}
        errorFallback="Failed to cancel partial recall."
      >
        <p className="text-sm text-gray-600">
          Cancel this pending {action ? noun(action.recall) : 'recall'} of{' '}
          <strong>{action?.recall.quantity.toLocaleString('en-US')}</strong>? It restores the
          markable remaining quantity. No Backoffice posting.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        show={action?.type === 'date'}
        onClose={close}
        title="Edit settlement date"
        confirmLabel="Save"
        isLoading={setDate.isPending}
        confirmDisabled={!dateValue}
        onConfirm={confirmDate}
        error={setDate.error}
        errorFallback="Failed to update settlement date."
      >
        <div className="space-y-2">
          <label htmlFor="recall-edit-date" className="block text-xs font-medium uppercase tracking-wide text-gray-500">
            Contractual settlement date <span className="normal-case text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="recall-edit-date"
            type="date"
            required
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            aria-required="true"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          />
          <p className="text-[11px] text-gray-500">
            The contractual date the recall is expected to settle. Settling itself is a manual click
            (Settle) — override this date so billing stays aligned when settlement is delayed.
          </p>
        </div>
      </ConfirmDialog>
    </section>
  );
}
